import type { BoardDto } from '@mm/shared';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createBoard,
  deleteBoard,
  duplicateBoard,
  listBoards,
  renameBoard,
} from '../api/boards';
import { Button } from '../ui/Button';
import { IconPlus } from '../ui/icons';

export function BoardsGrid({ studentId }: { studentId: string }) {
  const nav = useNavigate();
  const [boards, setBoards] = useState<BoardDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await listBoards(studentId);
    setBoards(r.items);
    setLoading(false);
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate() {
    setBusy(true);
    try {
      const b = await createBoard(studentId);
      nav(`/boards/${b.id}`);
    } finally {
      setBusy(false);
    }
  }

  async function onRename(b: BoardDto) {
    const next = prompt('Новое название', b.title);
    if (next == null || next.trim() === '' || next === b.title) return;
    await renameBoard(b.id, next.trim());
    await load();
  }

  async function onDuplicate(b: BoardDto) {
    await duplicateBoard(b.id);
    await load();
  }

  async function onDelete(b: BoardDto) {
    if (!confirm(`Удалить доску «${b.title}»?`)) return;
    await deleteBoard(b.id);
    await load();
  }

  return (
    <section>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <h2
            className="font-display text-[22px] leading-none font-medium"
            style={{ fontVariationSettings: '"opsz" 48, "SOFT" 10' }}
          >
            Доски
            {boards.length > 0 ? (
              <span className="text-muted font-light text-[14px] ml-1.5">— {boards.length}</span>
            ) : null}
          </h2>
          <p className="font-caps italic text-[12px] text-muted mt-0.5">
            Индивидуальные холсты для занятий
          </p>
        </div>
        <Button size="sm" onClick={onCreate} disabled={busy}>
          <IconPlus size={12} />
          Новая доска
        </Button>
      </div>

      {loading ? (
        <p className="text-muted text-[13px]">Загрузка…</p>
      ) : boards.length === 0 ? (
        <div className="rounded border-2 border-dashed border-border p-10 text-center">
          <span
            className="block font-display text-[56px] leading-none text-border mb-2"
            style={{ fontVariationSettings: '"opsz" 144' }}
          >
            ∑
          </span>
          <p className="text-muted text-[13px]">
            У ученика пока нет досок. Нажмите «Новая доска», чтобы начать.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {boards.map((b) => (
            <div
              key={b.id}
              className="rounded border border-border bg-bg overflow-hidden transition-colors hover:border-muted"
            >
              <button
                type="button"
                onClick={() => nav(`/boards/${b.id}`)}
                className="block w-full aspect-video bg-panel border-b border-border flex items-center justify-center text-muted text-[11px] font-caps italic hover:bg-row-hover transition-colors"
              >
                без превью
              </button>
              <div className="p-3 space-y-2">
                <button
                  type="button"
                  onClick={() => nav(`/boards/${b.id}`)}
                  className="block w-full text-left font-display text-[15px] font-medium text-fg hover:text-accent truncate"
                  style={{ fontVariationSettings: '"opsz" 24' }}
                  title={b.title}
                >
                  {b.title}
                </button>
                <p className="text-[11px] text-muted nums">
                  Обновлено {new Date(b.updatedAt).toLocaleDateString('ru')}
                </p>
                <div className="flex gap-0.5 flex-wrap pt-1 border-t border-border-soft">
                  <button
                    className="text-[11px] text-muted hover:text-accent px-1.5 py-0.5 rounded"
                    onClick={() => onRename(b)}
                  >
                    Переименовать
                  </button>
                  <button
                    className="text-[11px] text-muted hover:text-accent px-1.5 py-0.5 rounded"
                    onClick={() => onDuplicate(b)}
                  >
                    Дублировать
                  </button>
                  <button
                    className="text-[11px] text-muted hover:text-accent px-1.5 py-0.5 rounded"
                    onClick={() => onDelete(b)}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
